import type { ChangeEvent, ComponentProps } from "react"
import { useCaretRestore } from "../hooks/use-caret-restore.ts"
import { editPhone, formatPhone } from "../lib/phone-jp.ts"
import { Input } from "../ui/index.ts"

/**
 * 연락처 입력칸.
 *
 * 숫자를 치면 일본 번호 표기에 맞춰 하이픈이 따라 붙는다(`080-1234-1234`).
 * 어디서 끊는지는 `lib/phone-jp.ts` 가 정한다 — 일본 번호로 보이지 않으면 숫자를 그대로 둔다.
 * 값은 **보이는 모양 그대로** 돌려준다(저장도 같은 모양이다).
 */
export function PhoneInput({
  value,
  onChange,
  ...rest
}: {
  /** 저장되는 값. 하이픈이 붙은 모양 그대로다. */
  value: string
  onChange: (next: string) => void
} & Omit<ComponentProps<typeof Input>, "value" | "onChange" | "type">) {
  const { ref, setCaret } = useCaretRestore<HTMLInputElement>()

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const input = event.target
    const inputType = (event.nativeEvent as InputEvent).inputType ?? ""
    const result = editPhone({
      value: input.value,
      caret: input.selectionStart ?? input.value.length,
      deleting: inputType.startsWith("deleteContentBackward")
        ? "backward"
        : inputType.startsWith("deleteContentForward")
          ? "forward"
          : null,
      previousDigits: value.replace(/\D/g, ""),
    })

    if (result.formatted === value) {
      // 값이 그대로면(숫자 아닌 글자·자릿수 초과) 다시 그리지 않으므로 입력칸을 직접 되돌린다.
      input.value = result.formatted
      input.setSelectionRange(result.caret, result.caret)
      return
    }

    setCaret(result.caret)
    onChange(result.formatted)
  }

  return (
    <Input
      ref={ref}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      value={formatPhone(value) ?? ""}
      onChange={handleChange}
      {...rest}
    />
  )
}
